import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDisplayLong } from '../../../lib/dateUtils';
import EmailReportModal from '../../../components/reports/EmailReportModal';
import FinaliseRepairsModal from '../../../components/reports/FinaliseRepairsModal';
import Button from '../../../components/ui/Button';
import FilterChip from '../../../components/ui/FilterChip';
import Badge from '../../../components/ui/Badge';
import { useAuthStore } from '../../../store/authStore';
import { getElementTypeLabel } from '../../../constants/AssetTestConfig';
import { useReportRows } from '../hooks/useReportRows';
import { useReportExports, getReportTypeReportLabel } from '../hooks/useReportExports';
import { compareReportRows, ROW_KIND } from '../utils/reportRows';

const REPORT_SORT_KEYS = new Set(['date', 'site', 'technician', 'component', 'type', 'qty', 'fails']);

function parseSortFromSearchParams(searchParams) {
  const rawSort = searchParams.get('sort');
  const sortKey = REPORT_SORT_KEYS.has(rawSort) ? rawSort : 'date';
  const rawDir = searchParams.get('dir');
  const sortDir = rawDir === 'asc' || rawDir === 'desc' ? rawDir : 'desc';
  return { sortKey, sortDir };
}

export default function ReportsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const siteIdFromUrl = searchParams.get('siteId') || '';

  const [sessionTypeFilter, setSessionTypeFilter] = useState('all');
  const [emailModal, setEmailModal] = useState({ open: false, row: null, reportType: 'session' });
  const [finaliseModal, setFinaliseModal] = useState({ open: false, bucket: null });
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');

  const { sortKey, sortDir } = useMemo(
    () => parseSortFromSearchParams(searchParams),
    [searchParams],
  );

  const qParam = searchParams.get('q') || '';
  const prevQParamRef = useRef(null);
  useEffect(() => {
    if (prevQParamRef.current === null) {
      prevQParamRef.current = qParam;
      return;
    }
    if (prevQParamRef.current === qParam) return;
    prevQParamRef.current = qParam;
    setSearchInput(qParam);
  }, [qParam]);

  const flushSearchToUrl = useCallback(() => {
    const trimmed = searchInput.trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      return next;
    }, { replace: true });
  }, [searchInput, setSearchParams]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const current = prev.get('q') || '';
        if (trimmed === current) return prev;
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchInput, setSearchParams]);

  const setSortInUrl = useCallback((nextSort, nextDir) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('sort', nextSort);
      next.set('dir', nextDir);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const onToggleSortDir = useCallback(() => {
    const next = sortDir === 'asc' ? 'desc' : 'asc';
    setSortInUrl(sortKey, next);
  }, [sortDir, sortKey, setSortInUrl]);

  const {
    sites,
    sitesError,
    loadingSites,
    rows,
    isLoadingRows,
    sessionsError,
    bucketsError,
    openBucketsError,
  } = useReportRows();

  const siteById = useMemo(
    () => new Map((sites || []).map((s) => [String(s.id), s])),
    [sites],
  );

  const siteFilteredRows = useMemo(() => {
    if (!siteIdFromUrl) return rows;
    return rows.filter((row) => String(row.siteId) === String(siteIdFromUrl));
  }, [rows, siteIdFromUrl]);

  // "Repair" catches finalised and open repair buckets. Element-type chips
  // exclude those rows; this chip is the way to see only them.
  const filteredRows = useMemo(() => {
    if (sessionTypeFilter === 'all') return siteFilteredRows;
    if (sessionTypeFilter === 'repair') {
      return siteFilteredRows.filter((row) => row.reportType === 'repair');
    }
    return siteFilteredRows.filter((row) => row.reportType !== 'repair' && row.elementType === sessionTypeFilter);
  }, [siteFilteredRows, sessionTypeFilter]);

  const getReportTypeLabel = (reportType) => {
    if (reportType === 'defect') return 'Defect';
    if (reportType === 'repair') return 'Repair';
    return 'Testing';
  };

  const getRowReportTypes = (row) => {
    if (row.kind === ROW_KIND.FINALISED_BUCKET) return ['repair'];
    if (row.kind === ROW_KIND.OPEN_BUCKET) return [];
    if ((row.fails ?? 0) > 0) return ['session', 'defect'];
    return ['session'];
  };

  const searchFiltered = useMemo(() => {
    const needle = searchInput.trim().toLowerCase();
    if (!needle) return filteredRows;
    return filteredRows.filter((row) => {
      const haystack = [
        row.siteLabel,
        row.technician,
        getElementTypeLabel(row.elementType),
        row.badge.label,
        row.reportType,
        row.raw.work_order_number,
        row.raw.workOrderNumber,
        row.reportId,
      ]
        .filter((v) => v != null && String(v).trim() !== '')
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [filteredRows, searchInput]);

  const sortedRows = useMemo(() => {
    const list = [...searchFiltered];
    list.sort((a, b) => compareReportRows(a, b, sortKey, sortDir));
    return list;
  }, [searchFiltered, sortKey, sortDir]);

  // Sorting has nothing to act on once every filter/search step has zeroed out the list —
  // collapse the sort control rather than leave it offering to reorder an empty page.
  const hasZeroFilteredResults =
    !loadingSites
    && !isLoadingRows
    && Array.isArray(sites) && sites.length > 0
    && sortedRows.length === 0;

  const { handleExportSessionReport, handleSendEmailReport } = useReportExports();

  const handleGenerateReport = (row) => {
    setFinaliseModal({ open: true, bucket: row.raw });
  };

  const renderRowActions = (row) => {
    const reportTypes = getRowReportTypes(row);
    return (
      <div className="reports-actions">
        {reportTypes.map((rt) => (
          <Button
            key={`pdf-${rt}`}
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => handleExportSessionReport(row, rt)}
          >
            {getReportTypeLabel(rt)} PDF
          </Button>
        ))}
        {reportTypes.map((rt) => (
          <Button
            key={`email-${rt}`}
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setEmailModal({ open: true, row, reportType: rt })}
          >
            {getReportTypeLabel(rt)} Email
          </Button>
        ))}
        {row.kind === ROW_KIND.OPEN_BUCKET ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => handleGenerateReport(row)}>
            Generate report
          </Button>
        ) : null}
      </div>
    );
  };

  const renderTableRow = (row) => {
    const componentLabel = getElementTypeLabel(row.elementType);

    return (
      <tr key={row.key}>
        <td className="reports-table-site reports-table-col-site reports-table-col-ellipsis" title={row.siteLabel}>
          {row.siteLabel}
          {row.reportId ? <div className="text-muted">{row.reportId}</div> : null}
        </td>
        <td className="reports-table-col-type">
          <Badge variant={row.badge.variant}>{row.badge.label}</Badge>
        </td>
        <td className="reports-table-col-component reports-table-col-ellipsis" title={componentLabel}>
          {componentLabel}
        </td>
        <td className="reports-table-col-tech reports-table-col-ellipsis" title={row.technician || ''}>
          {row.technician}
        </td>
        <td className="reports-table-col-date">{formatDisplayLong(row.dateMs)}</td>
        <td className="reports-table-col-numeric reports-table-col-qty">{row.qty}</td>
        <td className="reports-table-col-numeric reports-table-col-fails">{row.fails == null ? '—' : row.fails}</td>
        <td className="reports-table-col-actions">{renderRowActions(row)}</td>
      </tr>
    );
  };

  const renderMobileCard = (row) => {
    const componentLabel = getElementTypeLabel(row.elementType);

    return (
      <li key={row.key} className="reports-mobile-card">
        <div className="reports-mobile-card-top">
          <h3 className="reports-mobile-card-site">{row.siteLabel}</h3>
          <Badge variant={row.badge.variant}>{row.badge.label}</Badge>
        </div>
        {row.reportId ? <p className="text-muted">{row.reportId}</p> : null}
        <p className="reports-mobile-card-meta">
          {row.technician || 'Unknown'} <span aria-hidden>•</span> {formatDisplayLong(row.dateMs)}{' '}
          <span aria-hidden>•</span> {row.qty} {row.kind === ROW_KIND.SESSION ? 'tested' : 'repairs'}{' '}
          <span aria-hidden>•</span>{' '}
          {row.fails == null ? '—' : `${row.fails} fail${row.fails === 1 ? '' : 's'}`}
        </p>
        <p className="reports-mobile-card-component">{componentLabel}</p>
        {renderRowActions(row)}
      </li>
    );
  };

  return (
    <div className="page">
      <div className="reports-header-row">
        <h1 className="page-title reports-page-title">Reports</h1>
        <div className="reports-chip-row" role="group" aria-label="Filter reports by component">
          <FilterChip active={sessionTypeFilter === 'all'} onClick={() => setSessionTypeFilter('all')}>
            All
          </FilterChip>
          <FilterChip active={sessionTypeFilter === 'electrical'} onClick={() => setSessionTypeFilter('electrical')}>
            Electrical
          </FilterChip>
          <FilterChip active={sessionTypeFilter === 'fitments'} onClick={() => setSessionTypeFilter('fitments')}>
            Workshop Equipment
          </FilterChip>
          <FilterChip active={sessionTypeFilter === 'repair'} onClick={() => setSessionTypeFilter('repair')}>
            Repair
          </FilterChip>
        </div>
      </div>

      {!loadingSites && Array.isArray(sites) && sites.length > 0 ? (
        <div className="reports-toolbar">
          <div className="form-group reports-toolbar-field">
            <label htmlFor="reports-search" className="sr-only">Search reports</label>
            <input
              id="reports-search"
              type="search"
              className="form-control"
              placeholder="Search site, technician, component…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onBlur={() => flushSearchToUrl()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  flushSearchToUrl();
                }
              }}
              autoComplete="off"
            />
          </div>
          {!hasZeroFilteredResults && (
            <div className="form-group reports-toolbar-field reports-toolbar-sort">
              <label htmlFor="reports-sort">Sort by</label>
              <div className="reports-toolbar-sort-row">
                <select
                  id="reports-sort"
                  className="form-control"
                  value={sortKey}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!REPORT_SORT_KEYS.has(v)) return;
                    setSortInUrl(v, sortDir);
                  }}
                >
                  <option value="date">Date</option>
                  <option value="site">Site</option>
                  <option value="technician">Technician</option>
                  <option value="component">Component</option>
                  <option value="type">Type</option>
                  <option value="qty">Asset QTY tested</option>
                  <option value="fails">Fails</option>
                </select>
                <Button
                  type="button"
                  variant="secondary"
                  className="reports-sort-dir-btn"
                  onClick={onToggleSortDir}
                  aria-label={sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'}
                >
                  {sortDir === 'asc' ? 'Ascending' : 'Descending'}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <section className="reports-list-card">
        {sitesError && <p className="page-error">Failed to load sites.</p>}
        {sessionsError && <p className="page-error">Failed to load reports.</p>}
        {bucketsError && <p className="page-error">Failed to load finalised repair reports.</p>}
        {openBucketsError && <p className="page-error">Failed to load pending repairs.</p>}
        {loadingSites && !sitesError ? (
          <p>Loading reports...</p>
        ) : !sitesError && Array.isArray(sites) && sites.length === 0 ? (
          <div className="reports-empty-state">
            <p className="reports-empty-state-message">No active sites available yet.</p>
            <Button type="button" variant="primary" onClick={() => navigate('/sites')}>
              Add a site
            </Button>
          </div>
        ) : null}
        {!loadingSites && isLoadingRows && sites?.length > 0 ? (
          <p>Loading reports...</p>
        ) : sites?.length > 0 && siteFilteredRows.length === 0 ? (
          <div className="reports-empty-state">
            <p className="reports-empty-state-message">
              {siteIdFromUrl
                ? 'No completed sessions for this site.'
                : 'No completed sessions across your sites yet.'}
            </p>
          </div>
        ) : sites?.length > 0 && filteredRows.length === 0 ? (
          <div className="reports-empty-state">
            <p className="reports-empty-state-message">No reports match this filter.</p>
          </div>
        ) : sites?.length > 0 && filteredRows.length > 0 && searchFiltered.length === 0 ? (
          <div className="reports-empty-state">
            <p className="reports-empty-state-message">No reports match your search.</p>
          </div>
        ) : sites?.length > 0 && sortedRows.length > 0 ? (
          <>
            <div className="reports-desktop-table-wrap">
              <table className="reports-table" aria-label="Reports list">
                <colgroup>
                  <col className="reports-table-col-site" />
                  <col className="reports-table-col-type" />
                  <col className="reports-table-col-component" />
                  <col className="reports-table-col-tech" />
                  <col className="reports-table-col-date" />
                  <col className="reports-table-col-qty" />
                  <col className="reports-table-col-fails" />
                  <col className="reports-table-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" className="reports-table-col-site">Site</th>
                    <th scope="col" className="reports-table-col-type">Type</th>
                    <th scope="col" className="reports-table-col-component">Component</th>
                    <th scope="col" className="reports-table-col-tech">Technician</th>
                    <th scope="col" className="reports-table-col-date">Date</th>
                    <th scope="col" className="reports-table-col-numeric reports-table-col-qty" title="Number of assets tested in this session">Asset QTY tested</th>
                    <th scope="col" className="reports-table-col-numeric reports-table-col-fails" title="Assets that did not pass">Fails</th>
                    <th scope="col" className="reports-table-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>{sortedRows.map(renderTableRow)}</tbody>
              </table>
            </div>
            <ul className="reports-mobile-list">{sortedRows.map(renderMobileCard)}</ul>
          </>
        ) : null}
      </section>

      <EmailReportModal
        isOpen={emailModal.open}
        onClose={() => setEmailModal({ open: false, row: null, reportType: 'session' })}
        reportTypeLabel={getReportTypeReportLabel(emailModal.reportType)}
        defaultRecipient={
          siteById.get(String(emailModal.row?.siteId))?.contact_email?.trim()
          || user?.email
          || ''
        }
        onSend={(recipientEmail) => handleSendEmailReport(emailModal.row, emailModal.reportType, recipientEmail)}
      />

      <FinaliseRepairsModal
        isOpen={finaliseModal.open}
        bucket={finaliseModal.bucket}
        onClose={() => setFinaliseModal({ open: false, bucket: null })}
      />
    </div>
  );
}
